import { resolveEnvelopeEmailFields } from '../../utils/docusignEmailDefaults';

// DocuSign rejects envelope email subjects longer than 100 characters.
export const DOCUSIGN_EMAIL_SUBJECT_MAX_LENGTH = 100;

export function formatEnvelopeTimestamp(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`;
  } catch {
    return null;
  }
}

export function getEnvelopeTimestampDisplay(envelope) {
  if (!envelope) return { label: 'Created', value: null };
  const { status } = envelope;
  if (status === 'draft' || status === 'tagging') {
    return { label: 'Created', value: formatEnvelopeTimestamp(envelope.createdAt) };
  }
  if (status === 'completed') {
    return {
      label: 'Completed',
      value: formatEnvelopeTimestamp(envelope.completedAt)
        || formatEnvelopeTimestamp(envelope.sentAt),
    };
  }
  return {
    label: 'Sent',
    value: formatEnvelopeTimestamp(envelope.sentAt)
      || formatEnvelopeTimestamp(envelope.createdAt),
  };
}

export function getEnvelopeListSubject(envelope, advisoryFirmName, emailOptions = {}) {
  return resolveEnvelopeEmailFields(envelope, {
    firmName: advisoryFirmName,
    ...emailOptions,
  }).subject;
}

export const ENVELOPE_STATUS_LABELS = {
  draft: 'Draft',
  tagging: 'In progress',
  sent: 'Sent',
  declined: 'Declined',
  voided: 'Voided',
  completed: 'Completed',
};

export const ENVELOPE_STATUS_BADGE_CLASS = {
  draft: 'bg-blue-500/15 border-blue-500/30 text-blue-800 dark:text-blue-200',
  tagging: 'bg-violet-500/15 border-violet-500/30 text-violet-800 dark:text-violet-200',
  sent: 'bg-amber-500/15 border-amber-500/30 text-amber-800 dark:text-amber-200',
  completed: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-800 dark:text-emerald-200',
  declined: 'bg-destructive/10 border-destructive/30 text-destructive',
  voided: 'bg-muted border-border text-muted-foreground',
};

export function getEnvelopeStatusBadgeClassName(status) {
  return ENVELOPE_STATUS_BADGE_CLASS[status] || '';
}

/** "Replaced" badge: a redone onboarding SOA went to the marketer in a newer envelope (workflows/handBack.cjs). */
export const ENVELOPE_REPLACED_BADGE_CLASS = 'bg-destructive/10 border-destructive/30 text-destructive';

/** Hover text for a replaced envelope, or null when it isn't one. Signed envelopes are never flagged. */
export function envelopeReplacedTitle(envelope) {
  if (!envelope?.replacedAt || envelope.status === 'completed' || envelope.status === 'voided') return null;
  const newer = envelope.replacedByName ? `“${envelope.replacedByName}”` : 'a newer envelope';
  return `Replaced by ${newer}: the SOA was redone. Don't send or present this one.`;
}

/** The confirm shown before sending or resending a replaced envelope, or null. */
export function replacedEnvelopeSendConfirm(envelope) {
  const title = envelopeReplacedTitle(envelope);
  if (!title) return null;
  return {
    title: 'This envelope was replaced',
    description: `${title} Send it anyway?`,
    confirmLabel: 'Send anyway',
    destructive: true,
  };
}
